if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('.sticky-atc__button');
        this.buttonText = this.querySelector('.sticky-atc__button-text');
        this.priceWrapper = this.querySelector('.sticky-atc__price-wrapper');
        this.variantSelect = this.querySelector('.sticky-atc__variant-select');
        this.qtyInput = this.querySelector('.sticky-atc__qty-input');
        this.qtyMinusBtn = this.querySelector('.sticky-atc__qty-btn[name="minus"]');
        this.qtyPlusBtn = this.querySelector('.sticky-atc__qty-btn[name="plus"]');
        this.productImage = this.querySelector('.sticky-atc__image');
        this.sectionId = this.dataset.sectionId;
        this.targetFormId = this.dataset.targetForm;
        this.buyButtonsId = this.dataset.buyButtonsId;
        this._syncingFromMain = false;
        // Intelligems: preserve product id so re-rendered price nodes keep ig tags
        this.productId = this.priceWrapper?.querySelector('[data-product-id]')?.dataset.productId || '';
      }

      connectedCallback() {
        this.setupIntersectionObserver();
        this.setupClickHandler();
        this.setupVariantChangeListener();
        this.setupVariantSelect();
        this.setupQuantityControls();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.variantChangeUnsubscribe?.();
        this.quantityUpdateUnsubscribe?.();
      }

      setupIntersectionObserver() {
        const buyButtons = document.getElementById(this.buyButtonsId);
        if (!buyButtons) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.hide();
              } else {
                const rect = entry.boundingClientRect;
                if (rect.top < 0 || rect.bottom < 0) {
                  this.show();
                }
              }
            });
          },
          { threshold: 0 }
        );

        this.observer.observe(buyButtons);
      }

      setupClickHandler() {
        this.button?.addEventListener('click', this.handleClick.bind(this));
      }

      handleClick() {
        if (this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') return;

        // Get variant ID from sticky select, or fall back to main form hidden input
        let variantId;
        if (this.variantSelect) {
          variantId = this.variantSelect.value;
        } else {
          const form = document.getElementById(this.targetFormId);
          variantId = form?.querySelector('input[name="id"]')?.value;
        }
        if (!variantId) return;

        const quantity = this.qtyInput ? parseInt(this.qtyInput.value) || 1 : 1;

        this.button.classList.add('loading');
        this.button.setAttribute('aria-disabled', 'true');

        // Direct fetch to /cart/add.js — bypasses Kaching bundle app interception
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);

        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Error (e.g. out of stock)
              if (typeof publish !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'sticky-atc',
                  productVariantId: variantId,
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              return;
            }

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (typeof publish !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'sticky-atc',
                productVariantId: variantId,
                cartData: response,
              });
            }

            cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.button.classList.remove('loading');
            this.button.removeAttribute('aria-disabled');
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          });
      }

      /* ── Variant select ── */

      setupVariantSelect() {
        if (!this.variantSelect) return;

        this.variantSelect.addEventListener('change', () => {
          if (this._syncingFromMain) return;

          const variantId = this.variantSelect.value;
          this.updateMainFormVariant(variantId);
        });
      }

      updateMainFormVariant(variantId) {
        // Find the main variant picker and programmatically select the matching variant
        const productInfo = document.querySelector(`product-info[data-section="${this.sectionId}"]`);
        if (!productInfo) return;

        const variantSelects = productInfo.querySelector('variant-selects');

        if (variantSelects) {
          // For products with option-based variant pickers, we need to find
          // which option combination matches this variant ID and select those options.
          // Try to find the variant data from the select options or the product JSON
          const selectedOption = this.variantSelect.querySelector(`option[value="${variantId}"]`);
          if (!selectedOption) return;

          // Use the variant title to determine option values
          // The variant title is formatted as "Option1 / Option2 / ..."
          const variantTitle = selectedOption.textContent.replace(/\s*—\s*.*$/, '').trim();
          const optionValues = variantTitle.split(' / ');

          // Try to match and select each option in the main variant picker
          const fieldsets = variantSelects.querySelectorAll('fieldset');
          const selects = variantSelects.querySelectorAll('select');

          let changed = false;

          // Handle radio/swatch inputs
          fieldsets.forEach((fieldset, index) => {
            if (optionValues[index] === undefined) return;
            const input = fieldset.querySelector(`input[value="${CSS.escape(optionValues[index])}"]`);
            if (input && !input.checked) {
              input.checked = true;
              changed = true;
            }
          });

          // Handle select dropdowns
          selects.forEach((select, index) => {
            if (optionValues[index] === undefined) return;
            const option = Array.from(select.options).find((opt) => opt.value === optionValues[index]);
            if (option && !option.selected) {
              select.value = optionValues[index];
              changed = true;
            }
          });

          if (changed) {
            // Temporarily block window.scrollTo so the media gallery
            // doesn't scroll the page when changing the active image
            const origScrollTo = window.scrollTo;
            window.scrollTo = function(opts) {
              // Allow horizontal scrolls (media gallery slider), block vertical page scrolls
              if (typeof opts === 'object' && opts.top !== undefined) return;
              origScrollTo.apply(window, arguments);
            };
            variantSelects.dispatchEvent(new Event('change', { bubbles: true }));
            // Restore after async callbacks have fired
            setTimeout(() => { window.scrollTo = origScrollTo; }, 1000);
          }
        } else {
          // Fallback: directly update the hidden variant input
          const form = document.getElementById(this.targetFormId);
          if (!form) return;
          const input = form.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      /* ── Quantity controls ── */

      setupQuantityControls() {
        if (!this.qtyInput) return;

        this.qtyMinusBtn?.addEventListener('click', () => {
          const min = parseInt(this.qtyInput.min) || 1;
          const step = parseInt(this.qtyInput.step) || 1;
          const currentVal = parseInt(this.qtyInput.value) || min;
          const newVal = Math.max(min, currentVal - step);
          this.qtyInput.value = newVal;
          this.syncQuantityToMainForm();
          this.updateQtyButtonStates();
        });

        this.qtyPlusBtn?.addEventListener('click', () => {
          const max = this.qtyInput.max ? parseInt(this.qtyInput.max) : Infinity;
          const step = parseInt(this.qtyInput.step) || 1;
          const currentVal = parseInt(this.qtyInput.value) || 1;
          const newVal = Math.min(max, currentVal + step);
          this.qtyInput.value = newVal;
          this.syncQuantityToMainForm();
          this.updateQtyButtonStates();
        });

        this.qtyInput.addEventListener('change', () => {
          this.syncQuantityToMainForm();
          this.updateQtyButtonStates();
        });

        // Listen for quantity changes from the main form
        this.observeMainQuantity();

        this.updateQtyButtonStates();
      }

      syncQuantityToMainForm() {
        const mainQtyInput = document.querySelector(
          `quantity-input[data-section="${this.sectionId}"] .quantity__input`
        );
        if (mainQtyInput && mainQtyInput.value !== this.qtyInput.value) {
          mainQtyInput.value = this.qtyInput.value;
          mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      observeMainQuantity() {
        const mainQtyInput = document.querySelector(
          `quantity-input[data-section="${this.sectionId}"] .quantity__input`
        );
        if (!mainQtyInput) return;

        // Use MutationObserver on value attribute + input event
        mainQtyInput.addEventListener('change', () => {
          if (this.qtyInput.value !== mainQtyInput.value) {
            this.qtyInput.value = mainQtyInput.value;
            this.updateQtyButtonStates();
          }
        });
      }

      updateQtyButtonStates() {
        if (!this.qtyInput) return;
        const val = parseInt(this.qtyInput.value) || 1;
        const min = parseInt(this.qtyInput.min) || 1;
        const max = this.qtyInput.max ? parseInt(this.qtyInput.max) : Infinity;

        this.qtyMinusBtn?.toggleAttribute('disabled', val <= min);
        this.qtyPlusBtn?.toggleAttribute('disabled', val >= max);
      }

      /* ── Variant change listener (from main form → sticky) ── */

      setupVariantChangeListener() {
        if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const { data } = event;
          if (data.sectionId !== this.sectionId) return;

          this.updateFromVariant(data.variant);
        });
      }

      updateFromVariant(variant) {
        if (!variant) {
          this.updateButtonState(false, window.variantStrings.unavailable);
          this.updateVariantSelect(null);
          return;
        }

        if (!variant.available) {
          this.updateButtonState(false, window.variantStrings.soldOut);
        } else {
          // Pre-order (DIEK-263): data-add-label on <sticky-atc> overrides the default label.
          this.updateButtonState(true, (this.dataset.addLabel || window.variantStrings.addToCart).toUpperCase());
        }

        this.updatePrice(variant.price, variant.compare_at_price, variant.id);
        this.updateVariantSelect(variant);
        this.updateProductImage(variant);
      }

      updateVariantSelect(variant) {
        if (!this.variantSelect) return;

        this._syncingFromMain = true;
        if (variant) {
          this.variantSelect.value = String(variant.id);
        }
        this._syncingFromMain = false;
      }

      updateProductImage(variant) {
        if (!this.productImage || !variant?.featured_image) return;
        const newSrc = variant.featured_image.src
          ? this.getSizedImageUrl(variant.featured_image.src, '100x')
          : null;
        if (newSrc) {
          this.productImage.src = newSrc;
          this.productImage.alt = variant.featured_image.alt || '';
        }
      }

      getSizedImageUrl(src, size) {
        // Shopify CDN image URL resizing
        if (src.includes('cdn.shopify.com')) {
          return src.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|\d*x\d+)\./, `_${size}.`).replace(/\.([a-zA-Z]+)\?/, `_${size}.$1?`);
        }
        return src;
      }

      updateButtonState(available, text) {
        if (!this.button || !this.buttonText) return;

        if (available) {
          this.button.removeAttribute('disabled');
          this.buttonText.innerHTML = `
            ${text}
            <span class="svg-wrapper" style="display: inline-block; width: 13px; height: 13px; margin-left: 8px; vertical-align: middle;">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.75 7.25H0V5.75H5.75V0H7.25V5.75H13V7.25H7.25V13H5.75V7.25Z" fill="currentColor"/>
              </svg>
            </span>
          `;
        } else {
          this.button.setAttribute('disabled', 'disabled');
          this.buttonText.textContent = text;
        }
      }

      updatePrice(price, compareAtPrice, variantId) {
        if (!this.priceWrapper) return;

        const formattedPrice = this.formatMoney(price);
        // Intelligems: re-apply data attributes so the plugin re-tags rebuilt nodes
        const igAttrs = `data-product-id="${this.productId}" data-variant-id="${variantId || ''}"`;

        if (compareAtPrice && compareAtPrice > price) {
          const formattedCompare = this.formatMoney(compareAtPrice);
          const style = this.dataset.savingsStyle || 'percentage';

          let badgeHTML;
          if (style === 'amount') {
            const saveAmount = this.formatMoneyNoZeros(compareAtPrice - price);
            badgeHTML = `<span class="sticky-atc__save-badge sticky-atc__save-badge--amount industry-bold igSavingsPrice" ${igAttrs}>You save ${saveAmount}</span>`;
          } else {
            const savePercent = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
            badgeHTML = `<span class="sticky-atc__save-badge igSavingsPercentage" ${igAttrs}>Save ${savePercent}%</span>`;
          }

          this.priceWrapper.innerHTML = `
            <span class="sticky-atc__price sticky-atc__price--sale igPrice" ${igAttrs}>${formattedPrice}</span>
            <span class="sticky-atc__compare-price igComparePrice" ${igAttrs}>${formattedCompare}</span>
            ${badgeHTML}
          `;
        } else {
          this.priceWrapper.innerHTML = `
            <span class="sticky-atc__price igPrice" ${igAttrs}>${formattedPrice}</span>
          `;
        }
      }

      formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);
        return `$${amount} AUD`;
      }

      formatMoneyNoZeros(cents) {
        const amount = (cents / 100).toFixed(2);
        const trimmed = amount.endsWith('.00') ? amount.slice(0, -3) : amount;
        return `$${trimmed}`;
      }

      show() {
        this.removeAttribute('hidden');
        requestAnimationFrame(() => {
          this.classList.add('sticky-atc--visible');
        });
      }

      hide() {
        this.classList.remove('sticky-atc--visible');
        this.addEventListener(
          'transitionend',
          () => {
            if (!this.classList.contains('sticky-atc--visible')) {
              this.setAttribute('hidden', '');
            }
          },
          { once: true }
        );
      }
    }
  );
}
